import { FunctionComponent } from 'react'
import styled from 'styled-components'
import Row from '../../../../components/Row'
import Text from '../../../../components/Text'
import ToggleButton from '../../../../components/ToggleButton'
import Button from '../../../../components/Button'
import TooltipWrapper from '../../../../components/TooltipWrapper'
import { styles } from '../../../../styles/themes/style'
import { INBOX_CONVERSATION_TAG_DEFAULTS } from '../../../../stores/inbox/inboxConversationTagDefaults'
import { useInboxSettingsHandlers } from '../../../../stores/inbox/useInboxSettingsStore'
import { useInboxHandlers } from '../../../../services/actions/inbox/useInboxHandlers'
import { useInboxStore } from '../../../../stores/inbox/useInboxStore'

interface IConversationTagsSettingsProps { }

const TAG_TOGGLE_TOOLTIP_WHEN_ON = 'Hide from Main inbox'
const TAG_TOGGLE_TOOLTIP_WHEN_OFF = 'Show in Main inbox'

const ConversationTags: FunctionComponent<IConversationTagsSettingsProps> = () => {
    const { inboxSettings, setDefaultView, setConversationTagEnabled, resetSettings } = useInboxSettingsHandlers()
    const { getConversationsFirstPage } = useInboxHandlers()
    const { store: { activeInboxLabel } } = useInboxStore()

    const refreshMainInboxIfActive = () => {
        if (activeInboxLabel === 'mainInbox') {
            void getConversationsFirstPage('mainInbox')
        }
    }

    const handleConversationTagToggle = (key: string, value: boolean) => {
        setConversationTagEnabled(key, value)
        refreshMainInboxIfActive()
    }

    const handleResetSettings = () => {
        resetSettings()
        refreshMainInboxIfActive()
    }

    return (
        <Container>
            <Section>
                <RowTitle>
                    <Text $label $capitalize>Default view</Text>
                    <SegmentedControl>
                        <TooltipWrapper tooltipText='Open Main inbox first' placement='top'>
                            <SegmentButton
                                type='button'
                                $active={inboxSettings.defaultView === 'main'}
                                onClick={() => setDefaultView('main')}
                            >
                                Main inbox
                            </SegmentButton>
                        </TooltipWrapper>
                        <TooltipWrapper tooltipText='Open All messages first' placement='top'>
                            <SegmentButton
                                type='button'
                                $active={inboxSettings.defaultView === 'all'}
                                onClick={() => setDefaultView('all')}
                            >
                                All messages
                            </SegmentButton>
                        </TooltipWrapper>
                    </SegmentedControl>
                </RowTitle>
                <RowContent>
                    <Text $lighter>Choose which view opens first when you launch LeadDelta Inbox:</Text>
                </RowContent>
            </Section>

            <HighlightedRow>
                <RowContent>
                    <Text $label $capitalize>{INBOX_CONVERSATION_TAG_DEFAULTS[0].title}</Text>
                    <Text $lighter>{INBOX_CONVERSATION_TAG_DEFAULTS[0].description}</Text>
                </RowContent>
            </HighlightedRow>

            <ListContainer>
                {INBOX_CONVERSATION_TAG_DEFAULTS.slice(1).map((setting, index) => (
                    <RowWrapper key={setting.key}>
                        <RowContent>
                            <Text $label $capitalize>{setting.title}</Text>
                            <Text $lighter>{setting.description}</Text>
                        </RowContent>
                        <ToggleButton
                            value={inboxSettings.conversationTags[setting.key]}
                            tooltipMessageOn={TAG_TOGGLE_TOOLTIP_WHEN_OFF}
                            tooltipMessageOff={TAG_TOGGLE_TOOLTIP_WHEN_ON}
                            onClickHandler={(_, value) => handleConversationTagToggle(setting.key, value)}
                        />
                        {index !== INBOX_CONVERSATION_TAG_DEFAULTS.length - 2 && <ListDivider />}
                    </RowWrapper>
                ))}
            </ListContainer>
            <Divider />
            <ResetRow>
                <RowContent>
                    <Text $label $capitalize>Reset settings</Text>
                    <Text $lighter>Restore all conversation tag toggles and the default view back to their original settings.</Text>
                </RowContent>
                <Button $type='light' $width='130px' onClickHandler={handleResetSettings}>
                    Reset to default
                </Button>
            </ResetRow>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    max-height: calc(100vh - 130px);
    overflow-y: auto;
    padding-right: 6px;

    @media (max-width: 1240px) {
        max-height: none;
        overflow-y: visible;
        padding-right: 0;
    }
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const RowTitle = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
`

const SegmentedControl = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    background: ${styles.colors.primary100};
    border-radius: 4px;
    padding: 2px;
    min-width: 198px;
`

const SegmentButton = styled.button<{ $active: boolean }>`
    width: 100%;
    height: 30px;
    border: none;
    border-radius: 4px;
    background: ${({ $active }) => ($active ? styles.colors.primary600 : 'transparent')};
    color: ${({ $active }) => ($active ? styles.colors.white : styles.colors.black400)};
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
`

const HighlightedRow = styled.div`
    display: flex;
    align-items: center;
    padding: 12px;
    border-radius: 4px;
    background: ${styles.colors.primary100};
`

const ListContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const RowWrapper = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px;
    align-items: start;
`

const RowContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 540px;
`

const Divider = styled.div`
    width: 100%;
    border-top: 1px solid ${styles.colors.black200};
`

const ListDivider = styled(Divider)`
    grid-column: 1 / -1;
`

const ResetRow = styled(Row)`
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`

export default ConversationTags
